import { useState, useEffect } from 'react';
import { Client, Vacancy, CopyVariant, Visual, ConsultantState } from '../consultantTypes';
import { ConsultantDashboard } from './pages/ConsultantDashboard';
import { Composer } from './pages/Composer';
import { UserManagement } from './pages/UserManagement';
import { createClient, getClients, createVacancy, getVacancies, shareVacancy } from '../lib/api/vacancies';
import { getAllConsultants } from '../lib/api/consultants';
import { createCopyVariant, getCopyVariantsByVacancy, updateCopyVariant, createVisual, getVisualsByVacancy, updateVisual, getFeedbackItemsByVisual, getFeedbackItemsByCopyVariant, createVisualVersion, getLatestVersionNumber, createFeedbackResolution, updateFeedbackItem, createUpdateNotification, createFeedbackItem } from '../lib/api/content';
import { uploadImage } from '../lib/storage';
import type { FeedbackItem } from '../consultantTypes';
import { logout, hasPermission } from '../lib/api/auth';
import type { DbClient, DbVacancy } from '../lib/api/vacancies';
import type { DbConsultant } from '../lib/api/consultants';
import type { User as AuthUser } from '../lib/api/auth';
import { User, LogOut } from 'lucide-react';
import { NotificationBell } from './components/NotificationBell';

interface ConsultantAppProps {
  user: AuthUser;
  onLogout: () => void;
}


export function ConsultantApp({ user, onLogout }: ConsultantAppProps) {
  const [consultants, setConsultants] = useState<DbConsultant[]>([]);
  const [selectedConsultantId, setSelectedConsultantId] = useState<string>(user.id);
  const isOwner = user.role === 'owner';

  const [state, setState] = useState<ConsultantState>({
    clients: [],
    vacancies: [],
    copyVariants: [],
    visuals: []
  });

  const [loading, setLoading] = useState(true);

  const [currentView, setCurrentView] = useState<{
    type: 'dashboard' | 'composer' | 'users';
    clientId?: string;
    vacancyId?: string;
  }>({ type: 'dashboard' });

  useEffect(() => {
    if (isOwner) {
      loadConsultants();
    } else {
      setLoading(false);
      loadData();
    }
  }, []);

  useEffect(() => {
    if (selectedConsultantId) {
      loadData();
    }
  }, [selectedConsultantId]);

  const loadConsultants = async () => {
    const dbConsultants = await getAllConsultants();
    setConsultants(dbConsultants);

    if (dbConsultants.length > 0) {
      const currentUserIsConsultant = dbConsultants.some(c => c.id === user.id);
      if (currentUserIsConsultant) {
        setSelectedConsultantId(user.id);
      } else {
        setSelectedConsultantId(dbConsultants[0].id);
      }
    } else {
      setSelectedConsultantId(user.id);
    }

    setLoading(false);
    loadData();
  };

  const loadData = async () => {
    const [dbClients, dbVacancies] = await Promise.all([
      getClients(),
      getVacancies()
    ]);

    const clients: Client[] = dbClients.map((c: DbClient) => ({
      id: c.id,
      name: c.name
    }));

    const vacancies: Vacancy[] = dbVacancies.map((v: any) => ({
      id: v.id,
      clientId: v.client_id,
      title: v.title,
      vacancyText: v.vacancy_text,
      notes: v.notes,
      shared: v.shared,
      shareToken: v.share_token,
      reviewCompletedAt: v.review_completed_at
    }));

    const allCopyVariantsPromises = vacancies.map(v => getCopyVariantsByVacancy(v.id));
    const allVisualsPromises = vacancies.map(v => getVisualsByVacancy(v.id));

    const [allDbCopyVariants, allDbVisuals] = await Promise.all([
      Promise.all(allCopyVariantsPromises),
      Promise.all(allVisualsPromises)
    ]);

    const copyVariants: CopyVariant[] = allDbCopyVariants.flat().map(cv => ({
      id: cv.id,
      vacancyId: cv.vacancy_id,
      title: cv.title,
      body: cv.body,
      source: cv.source,
      selected: cv.selected,
      status: cv.status,
      feedback: cv.feedback
    }));

    const visuals: Visual[] = allDbVisuals.flat().map(v => ({
      id: v.id,
      vacancyId: v.vacancy_id,
      title: v.title,
      url: v.url,
      status: v.status,
      selected: v.selected,
      feedback: v.feedback
    }));

    const visualsWithFeedback = await loadFeedbackForVisuals(visuals);

    setState(prev => ({
      ...prev,
      clients,
      vacancies,
      copyVariants,
      visuals: visualsWithFeedback
    }));
  };

  const loadFeedbackForVisuals = async (visuals: Visual[]): Promise<Visual[]> => {
    const feedbackPromises = visuals.map(async (visual) => {
      const feedbackItems = await getFeedbackItemsByVisual(visual.id);
      const latestVersion = await getLatestVersionNumber(visual.id);

      return {
        ...visual,
        feedbackItems: feedbackItems.map(f => ({
          id: f.id,
          visualId: f.visual_id,
          feedbackText: f.feedback_text,
          status: f.status,
          createdBy: f.created_by,
          createdAt: f.created_at,
          resolvedAt: f.resolved_at
        })),
        currentVersion: latestVersion,
        hasUpdate: visual.status === 'UPLOADED' && feedbackItems.some(f => f.status === 'resolved')
      };
    });

    return Promise.all(feedbackPromises);
  };

  const handleAddClient = async (name: string) => {
    const consultantIdToUse = isOwner ? selectedConsultantId : user.id;
    if (!consultantIdToUse) return;

    const newClient = await createClient(consultantIdToUse, name);
    if (newClient) {
      setState(prev => ({
        ...prev,
        clients: [...prev.clients, { id: newClient.id, name: newClient.name }]
      }));
    }
  };

  const handleAddVacancy = async (clientId: string, data: { title: string; vacancyText: string; notes: string }) => {
    const newVacancy = await createVacancy(clientId, {
      title: data.title,
      vacancy_text: data.vacancyText,
      notes: data.notes
    });

    if (newVacancy) {
      const vacancy: Vacancy = {
        id: newVacancy.id,
        clientId: newVacancy.client_id,
        title: newVacancy.title,
        vacancyText: newVacancy.vacancy_text,
        notes: newVacancy.notes,
        shared: newVacancy.shared,
        shareToken: newVacancy.share_token
      };
      setState(prev => ({
        ...prev,
      vacancies: [...prev.vacancies, vacancy]
    }));
    }
  };

  const handleAddManualCopy = async (vacancyId: string, title: string, body: string) => {
    const dbVariant = await createCopyVariant(vacancyId, {
      title,
      body,
      source: 'MANUAL'
    });

    if (dbVariant) {
      const newVariant: CopyVariant = {
        id: dbVariant.id,
        vacancyId: dbVariant.vacancy_id,
        title: dbVariant.title,
        body: dbVariant.body,
        source: dbVariant.source,
        selected: dbVariant.selected,
        status: dbVariant.status,
        feedback: dbVariant.feedback
      };

      setState(prev => ({
        ...prev,
        copyVariants: [...prev.copyVariants, newVariant]
      }));
    }
  };

  const handleToggleSelect = async (id: string) => {
    const variant = state.copyVariants.find(c => c.id === id);
    if (!variant) return;

    const selectedCount = state.copyVariants.filter(c => c.selected && c.vacancyId === variant.vacancyId).length;

    if (!variant.selected && selectedCount >= 4) {
      return;
    }

    const newSelected = !variant.selected;
    await updateCopyVariant(id, { selected: newSelected });

    setState(prev => ({
      ...prev,
      copyVariants: prev.copyVariants.map(c =>
        c.id === id ? { ...c, selected: newSelected } : c
      )
    }));
  };

  const handleToggleVisualSelect = async (id: string) => {
    const visual = state.visuals.find(v => v.id === id);
    if (!visual) return;

    const selectedCount = state.visuals.filter(v => v.selected && v.vacancyId === visual.vacancyId).length;

    if (!visual.selected && selectedCount >= 4) {
      return;
    }

    const newSelected = !visual.selected;
    await updateVisual(id, { selected: newSelected });

    setState(prev => ({
      ...prev,
      visuals: prev.visuals.map(v =>
        v.id === id ? { ...v, selected: newSelected } : v
      )
    }));
  };

  const handleEditCopy = async (id: string, body: string) => {
    const copyVariant = state.copyVariants.find(c => c.id === id);

    const feedbackItems = await getFeedbackItemsByCopyVariant(id);
    const openFeedbackItems = feedbackItems.filter(f => f.status === 'open');

    for (const feedbackItem of openFeedbackItems) {
      await updateFeedbackItem(feedbackItem.id, {
        status: 'resolved',
        resolved_at: new Date().toISOString()
      });
    }

    await updateCopyVariant(id, {
      body,
      source: 'MANUAL',
      status: copyVariant?.status === 'rejected' ? 'pending' : copyVariant?.status
    });

    setState(prev => ({
      ...prev,
      copyVariants: prev.copyVariants.map(c =>
        c.id === id ? {
          ...c,
          body,
          source: 'MANUAL' as const,
          status: c.status === 'rejected' ? 'pending' as const : c.status
        } : c
      )
    }));
  };

  const handleUploadVisual = async (vacancyId: string, file: File) => {
    const publicUrl = await uploadImage(file, 'visuals');

    if (!publicUrl) {
      alert('Fout bij het uploaden van de afbeelding. Probeer het opnieuw.');
      return;
    }

    const dbVisual = await createVisual(vacancyId, {
      title: file.name,
      url: publicUrl
    });

    if (dbVisual) {
      const newVisual: Visual = {
        id: dbVisual.id,
        vacancyId: dbVisual.vacancy_id,
        title: dbVisual.title,
        url: dbVisual.url,
        status: dbVisual.status,
        selected: dbVisual.selected,
        feedback: dbVisual.feedback,
        feedbackItems: [],
        currentVersion: 1
      };

      setState(prev => ({
        ...prev,
        visuals: [...prev.visuals, newVisual]
      }));
    }
  };

  const handleUploadNewVersion = async (visualId: string, file: File, resolvedFeedbackIds: string[]) => {
    const visual = state.visuals.find(v => v.id === visualId);
    if (!visual) return;

    const publicUrl = await uploadImage(file, 'visuals');

    if (!publicUrl) {
      alert('Fout bij het uploaden van de afbeelding. Probeer het opnieuw.');
      return;
    }

    const latestVersion = await getLatestVersionNumber(visualId);
    const newVersionNumber = latestVersion + 1;

    const newVersion = await createVisualVersion(visualId, publicUrl, newVersionNumber);

    if (!newVersion) {
      alert('Fout bij het aanmaken van de nieuwe versie.');
      return;
    }

    await updateVisual(visualId, {
      url: publicUrl,
      status: 'UPLOADED'
    });

    for (const feedbackId of resolvedFeedbackIds) {
      await updateFeedbackItem(feedbackId, {
        status: 'resolved',
        resolved_at: new Date().toISOString()
      });

      await createFeedbackResolution(feedbackId, newVersion.id);
    }

    if (resolvedFeedbackIds.length > 0) {
      const message = `${resolvedFeedbackIds.length} feedback${resolvedFeedbackIds.length > 1 ? 'punten zijn' : 'punt is'} verwerkt in ${visual.title}`;
      await createUpdateNotification(visual.vacancyId, visualId, message);
    }

    const updatedFeedbackItems = await getFeedbackItemsByVisual(visualId);

    setState(prev => ({
      ...prev,
      visuals: prev.visuals.map(v =>
        v.id === visualId
          ? {
              ...v,
              url: publicUrl,
              status: 'UPLOADED' as const,
              currentVersion: newVersionNumber,
              hasUpdate: true,
              feedbackItems: updatedFeedbackItems.map(f => ({
                id: f.id,
                visualId: f.visual_id,
                feedbackText: f.feedback_text,
                status: f.status,
                createdBy: f.created_by,
                createdAt: f.created_at,
                resolvedAt: f.resolved_at
              }))
            }
          : v
      )
    }));
  };

  const handleShare = async (vacancyId: string) => {
    const selectedCopyVariants = state.copyVariants.filter(cv => cv.selected && cv.vacancyId === vacancyId);
    const vacancyVisuals = state.visuals.filter(v => v.vacancyId === vacancyId);

    const rejectedCopyVariants = selectedCopyVariants.filter(cv => cv.status === 'rejected');
    const rejectedVisuals = vacancyVisuals.filter(v => v.status === 'REJECTED');

    for (const variant of rejectedCopyVariants) {
      await updateCopyVariant(variant.id, { status: 'pending' });
    }

    for (const visual of rejectedVisuals) {
      await updateVisual(visual.id, { status: 'UPLOADED' });
    }

    const success = await shareVacancy(vacancyId);

    if (success) {
      const shareableLink = `${window.location.origin}/client?vacancyId=${vacancyId}`;

      setState(prev => ({
        ...prev,
        vacancies: prev.vacancies.map(v =>
          v.id === vacancyId ? { ...v, shared: true } : v
        ),
        copyVariants: prev.copyVariants.map(cv =>
          rejectedCopyVariants.find(rcv => rcv.id === cv.id)
            ? { ...cv, status: 'pending' as const }
            : cv
        ),
        visuals: prev.visuals.map(v =>
          rejectedVisuals.find(rv => rv.id === v.id)
            ? { ...v, status: 'UPLOADED' as const }
            : v
        )
      }));

      const linkMessage = `Pakket succesvol gedeeld met de klant!\n\nDeel deze link met de klant:\n${shareableLink}`;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareableLink);
        alert(`${linkMessage}\n\n✓ Link gekopieerd naar klembord!`);
      } else {
        alert(linkMessage);
      }
    } else {
      alert('Er is iets misgegaan bij het delen van de vacature.');
    }
  };

  const handleOpenComposer = async (clientId: string, vacancyId: string) => {
    const [dbCopyVariants, dbVisuals] = await Promise.all([
      getCopyVariantsByVacancy(vacancyId),
      getVisualsByVacancy(vacancyId)
    ]);

    const copyVariants: CopyVariant[] = dbCopyVariants.map(cv => ({
      id: cv.id,
      vacancyId: cv.vacancy_id,
      title: cv.title,
      body: cv.body,
      source: cv.source,
      selected: cv.selected,
      status: cv.status,
      feedback: cv.feedback
    }));

    const visuals: Visual[] = dbVisuals.map(v => ({
      id: v.id,
      vacancyId: v.vacancy_id,
      title: v.title,
      url: v.url,
      status: v.status,
      selected: v.selected,
      feedback: v.feedback
    }));

    const visualsWithFeedback = await loadFeedbackForVisuals(visuals);

    setState(prev => ({
      ...prev,
      copyVariants: copyVariants.filter(cv => cv.vacancyId === vacancyId),
      visuals: visualsWithFeedback.filter(v => v.vacancyId === vacancyId)
    }));

    setCurrentView({ type: 'composer', clientId, vacancyId });
  };

  const handleBackToDashboard = async () => {
    await loadData();
    setCurrentView({ type: 'dashboard' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  const effectiveConsultants = consultants.length > 0
    ? consultants
    : (user.role === 'owner' || user.role === 'consultant')
    ? [{
        id: user.id,
        email: user.email,
        name: user.name,
        created_at: new Date().toISOString()
      }]
    : [];

  if (effectiveConsultants.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Geen consultants gevonden</h2>
          <p className="text-gray-600">Neem contact op met de administrator.</p>
        </div>
      </div>
    );
  }

  const selectedConsultant = effectiveConsultants.find(c => c.id === selectedConsultantId);

  if (currentView.type === 'composer' && currentView.clientId && currentView.vacancyId) {
    const client = state.clients.find(c => c.id === currentView.clientId);
    const vacancy = state.vacancies.find(v => v.id === currentView.vacancyId);

    if (!client || !vacancy) {
      return null;
    }

    const copyVariants = state.copyVariants.filter(c => c.vacancyId === currentView.vacancyId);
    const visuals = state.visuals.filter(v => v.vacancyId === currentView.vacancyId);

    const handleSwitchVacancy = async (newVacancyId: string) => {
      const [dbCopyVariants, dbVisuals] = await Promise.all([
        getCopyVariantsByVacancy(newVacancyId),
        getVisualsByVacancy(newVacancyId)
      ]);

      const copyVariants: CopyVariant[] = dbCopyVariants.map(cv => ({
        id: cv.id,
        vacancyId: cv.vacancy_id,
        title: cv.title,
        body: cv.body,
        source: cv.source,
        selected: cv.selected,
        status: cv.status,
        feedback: cv.feedback
      }));

      const visuals: Visual[] = dbVisuals.map(v => ({
        id: v.id,
        vacancyId: v.vacancy_id,
        title: v.title,
        url: v.url,
        status: v.status,
        selected: v.selected,
        feedback: v.feedback
      }));

      const visualsWithFeedback = await loadFeedbackForVisuals(visuals);

      setState(prev => ({
        ...prev,
        copyVariants: [
          ...prev.copyVariants.filter(cv => cv.vacancyId !== newVacancyId),
          ...copyVariants
        ],
        visuals: [
          ...prev.visuals.filter(v => v.vacancyId !== newVacancyId),
          ...visualsWithFeedback
        ]
      }));

      setCurrentView({ type: 'composer', clientId: currentView.clientId, vacancyId: newVacancyId });
    };

    return (
      <Composer
        client={client}
        vacancy={vacancy}
        allVacancies={state.vacancies}
        copyVariants={copyVariants}
        visuals={visuals}
        allCopyVariants={state.copyVariants}
        allVisuals={state.visuals}
        onBack={handleBackToDashboard}
        onAddManualCopy={handleAddManualCopy}
        onToggleSelect={handleToggleSelect}
        onToggleVisualSelect={handleToggleVisualSelect}
        onEditCopy={handleEditCopy}
        onUploadVisual={handleUploadVisual}
        onUploadNewVersion={handleUploadNewVersion}
        onShare={handleShare}
        onSwitchVacancy={handleSwitchVacancy}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img
            src="/NEEDLES Logo (Rechthoekig).svg"
            alt="Needles Logo"
            className="h-8"
          />
          <div className="flex items-center gap-6">
            {isOwner && (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-600" />
                <select
                  value={selectedConsultantId}
                  onChange={(e) => setSelectedConsultantId(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {effectiveConsultants.map(consultant => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={`flex items-center gap-4 ${isOwner ? 'pl-6 border-l border-gray-200' : ''}`}>
              {isOwner && (
                <button
                  onClick={() => setCurrentView({ type: currentView.type === 'users' ? 'dashboard' : 'users' })}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentView.type === 'users'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {currentView.type === 'users' ? 'Dashboard' : 'Gebruikers'}
                </button>
              )}
              <NotificationBell userId={user.id} />
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user.role}</p>
              </div>
              <button
                onClick={() => {
                  logout();
                  onLogout();
                }}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Uitloggen</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {currentView.type === 'users' ? (
        <UserManagement />
      ) : (
        <ConsultantDashboard
          clients={state.clients}
          vacancies={state.vacancies}
          copyVariants={state.copyVariants}
          visuals={state.visuals}
          onAddClient={handleAddClient}
          onAddVacancy={handleAddVacancy}
          onOpenComposer={handleOpenComposer}
        />
      )}
    </div>
  );
}
